<template>
  <div class="charting-feedback-layer" aria-live="polite">
    <transition-group name="charting-toast" tag="div" class="charting-feedback-stack">
      <article
        v-for="message in state.messages"
        :key="message.id"
        class="charting-feedback-toast"
        :class="`charting-feedback-toast--${message.severity}`"
        role="status"
      >
        <span class="charting-feedback-toast__icon" :class="toastIcon(message.severity)" aria-hidden="true"></span>
        <span class="charting-feedback-toast__copy">
          <strong v-if="message.summary">{{ message.summary }}</strong>
          <span v-if="message.detail">{{ message.detail }}</span>
        </span>
        <button type="button" class="charting-feedback-toast__close" aria-label="Cerrar aviso" @click="dismiss(message.id)">
          <span class="pi pi-times" aria-hidden="true"></span>
        </button>
      </article>
    </transition-group>

    <div v-if="state.confirmation" class="charting-confirm-mask" role="presentation" @click.self="rejectConfirmation">
      <section class="charting-confirm-dialog" role="alertdialog" aria-modal="true" :aria-labelledby="confirmationTitleId">
        <header class="charting-confirm-dialog__header">
          <span class="pi pi-exclamation-triangle" aria-hidden="true"></span>
          <strong :id="confirmationTitleId">{{ state.confirmation.header || 'Confirmación' }}</strong>
        </header>
        <p>{{ state.confirmation.message || '¿Quieres continuar?' }}</p>
        <footer class="charting-confirm-dialog__actions">
          <Button severity="secondary" outlined :label="state.confirmation.rejectLabel || 'Cancelar'" @click="rejectConfirmation" />
          <Button severity="danger" :label="state.confirmation.acceptLabel || 'Aceptar'" @click="acceptConfirmation" />
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import {
  acceptChartingConfirmation,
  chartingFeedbackState,
  dismissChartingFeedback,
  rejectChartingConfirmation
} from '@/features/charting-next/services/chartingFeedback';

export default {
  name: 'ChartingFeedbackLayer',
  data() {
    return {
      state: chartingFeedbackState,
      confirmationTitleId: 'charting-confirmation-title'
    };
  },
  methods: {
    dismiss: dismissChartingFeedback,
    acceptConfirmation: acceptChartingConfirmation,
    rejectConfirmation: rejectChartingConfirmation,
    toastIcon(severity) {
      return {
        success: 'pi pi-check-circle',
        error: 'pi pi-times-circle',
        warn: 'pi pi-exclamation-triangle',
        warning: 'pi pi-exclamation-triangle'
      }[severity] || 'pi pi-info-circle';
    }
  }
};
</script>

<style scoped>
.charting-feedback-layer { position: relative; z-index: 1500; }
.charting-feedback-stack {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1500;
  display: flex;
  width: min(24rem, calc(100vw - 2rem));
  flex-direction: column;
  gap: 0.65rem;
}
.charting-feedback-toast {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  border: 1px solid var(--charting-b-border-color, #cbd5e1);
  border-left: 0.3rem solid var(--p-primary-500, #00add9);
  border-radius: 0.85rem;
  background: var(--charting-b-panel-bg-strong, #fff);
  padding: 0.85rem;
  color: var(--p-text-color, #0f172a);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
}
.charting-feedback-toast--success { border-left-color: #22c55e; }
.charting-feedback-toast--error { border-left-color: #ef4444; }
.charting-feedback-toast--warn,
.charting-feedback-toast--warning { border-left-color: #f59e0b; }
.charting-feedback-toast__icon { margin-top: 0.16rem; font-size: 1.1rem; }
.charting-feedback-toast__copy { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 0.15rem; }
.charting-feedback-toast__copy span { color: var(--p-text-muted-color, #64748b); font-size: 0.85rem; }
.charting-feedback-toast__close { border: 0; background: transparent; color: inherit; cursor: pointer; }
.charting-confirm-mask {
  position: fixed;
  z-index: 1550;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.56);
}
.charting-confirm-dialog {
  width: min(30rem, 100%);
  border: 1px solid var(--charting-b-border-color, #cbd5e1);
  border-radius: 1rem;
  background: var(--charting-b-panel-bg-strong, #fff);
  padding: 1.25rem;
  color: var(--p-text-color, #0f172a);
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.32);
}
.charting-confirm-dialog__header { display: flex; align-items: center; gap: 0.65rem; font-size: 1.05rem; }
.charting-confirm-dialog__header .pi { color: #f59e0b; font-size: 1.25rem; }
.charting-confirm-dialog p { margin: 1rem 0 1.25rem; color: var(--p-text-muted-color, #64748b); }
.charting-confirm-dialog__actions { display: flex; justify-content: flex-end; gap: 0.65rem; }
.charting-toast-enter-active,
.charting-toast-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.charting-toast-enter,
.charting-toast-leave-to { opacity: 0; transform: translateX(1rem); }
@media (max-width: 640px) {
  .charting-feedback-stack { top: 0.65rem; right: 0.65rem; width: calc(100vw - 1.3rem); }
}
</style>
